import React, { useMemo } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import Underline from "@tiptap/extension-underline";
import {
    TIPTAP_FORMAT,
    resolveTipTapInitialContent,
} from "./blogContentFormat";
import "./BlogEditor.css";

const TipTapDescriptionBox = ({ content, onChange }) => {
    const initialContent = useMemo(
        () => resolveTipTapInitialContent(content),
        // Only resolve once on mount — editor owns state after that
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    const editor = useEditor({
        extensions: [
            StarterKit.configure({ heading: { levels: [2, 3] } }),
            Underline,
            Link.configure({ openOnClick: false, autolink: true }),
            Placeholder.configure({ placeholder: "Write your story…" }),
        ],
        content: initialContent,
        onCreate: ({ editor: ed }) => {
            onChange({
                format: TIPTAP_FORMAT,
                json: ed.getJSON(),
                html: ed.getHTML(),
            });
        },
        onUpdate: ({ editor: ed }) => {
            onChange({
                format: TIPTAP_FORMAT,
                json: ed.getJSON(),
                html: ed.getHTML(),
            });
        },
    });

    if (!editor) return null;

    const setLink = () => {
        const previous = editor.getAttributes("link").href;
        const url = window.prompt("Link URL", previous || "https://");
        if (url === null) return;
        if (url === "") {
            editor.chain().focus().extendMarkRange("link").unsetLink().run();
            return;
        }
        editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
    };

    return (
        <div className="blog-editor-wrap blog-tiptap-wrap">
            <div className="blog-tiptap-toolbar toolbar">
                <button
                    type="button"
                    className={editor.isActive("bold") ? "is-active" : ""}
                    onClick={() => editor.chain().focus().toggleBold().run()}
                    title="Bold"
                >
                    B
                </button>
                <button
                    type="button"
                    className={editor.isActive("italic") ? "is-active" : ""}
                    onClick={() => editor.chain().focus().toggleItalic().run()}
                    title="Italic"
                >
                    I
                </button>
                <button
                    type="button"
                    className={editor.isActive("underline") ? "is-active" : ""}
                    onClick={() => editor.chain().focus().toggleUnderline().run()}
                    title="Underline"
                >
                    U
                </button>
                <button
                    type="button"
                    className={editor.isActive("heading", { level: 2 }) ? "is-active" : ""}
                    onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
                    title="Heading"
                >
                    H2
                </button>
                <button
                    type="button"
                    className={editor.isActive("bulletList") ? "is-active" : ""}
                    onClick={() => editor.chain().focus().toggleBulletList().run()}
                    title="Bullet list"
                >
                    • List
                </button>
                <button
                    type="button"
                    className={editor.isActive("orderedList") ? "is-active" : ""}
                    onClick={() => editor.chain().focus().toggleOrderedList().run()}
                    title="Numbered list"
                >
                    1. List
                </button>
                <button
                    type="button"
                    className={editor.isActive("link") ? "is-active" : ""}
                    onClick={setLink}
                    title="Link"
                >
                    Link
                </button>
            </div>
            <EditorContent editor={editor} className="editor blog-tiptap-editor" />
        </div>
    );
};

export default TipTapDescriptionBox;
